// src/FacebookAITesting.js
import React, { useState, useEffect, useRef } from 'react';
import { apiFetch } from './utils/apiUtils';
import {
  Container,
  Typography,
  Button,
  Alert,
  Box,
  Paper,
  CircularProgress,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
  TextField,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  FormControlLabel,
  Switch
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import CategoryIcon from '@mui/icons-material/Category';
import SettingsIcon from '@mui/icons-material/Settings';
import TestIcon from '@mui/icons-material/BugReport';
import RefreshIcon from '@mui/icons-material/Refresh';
import UploadIcon from '@mui/icons-material/Upload';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PreviewIcon from '@mui/icons-material/Visibility';
import CalendarIcon from '@mui/icons-material/CalendarToday';
import VersionIcon from '@mui/icons-material/Layers';
import CheckIcon from '@mui/icons-material/Check';
import ErrorIcon from '@mui/icons-material/Error';
import { styled } from '@mui/material/styles';

// Styled component for file input
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const FacebookAITesting = () => {
  // File upload ref
  const fileInputRef = useRef(null);

  // Main state
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  
  // Response source state
  const [responseSource, setResponseSource] = useState(null);
  
  // Vector store cleanup state
  const [vectorStoreCleanupStatus, setVectorStoreCleanupStatus] = useState({
    isLoading: false,
    result: null,
    error: null
  });
  
  // OpenAI connection status
  const [openAIStatus, setOpenAIStatus] = useState({
    checking: true,
    apiKeyExists: false,
    apiClientInitialized: false,
    connectionSuccessful: false,
    error: null
  });

  // Analysis state  
  const [analysisResults, setAnalysisResults] = useState(null);
  const [sampleConversations, setSampleConversations] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Parameters state
  const [categories, setCategories] = useState([]);
  const [parameters, setParameters] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);

  // Dialog states
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', description: '' });
  
  // Parameter dialog states
  const [parameterDialogOpen, setParameterDialogOpen] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [editingParameterId, setEditingParameterId] = useState(null);
  const [filePreviewDialogOpen, setFilePreviewDialogOpen] = useState(false);
  
  // New parameter state
  const [parameterData, setParameterData] = useState({
    name: '',
    description: '',
    isActive: true,
    effectiveDate: new Date().toISOString().split('T')[0], // Today's date
    expirationDate: '',
    contextNotes: '',
    file: null
  });

  // Testing state
  const [testMessage, setTestMessage] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [testResults, setTestResults] = useState([]);
  const [testDate, setTestDate] = useState(new Date().toISOString().split('T')[0]); // Today's date
  const [selectedModel, setSelectedModel] = useState("gpt-3.5-turbo"); // Default model

  // Handle tab changes
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Run the Facebook message analysis
  const runAnalysis = async () => {
    try {
      setLoading(true);
      setError("");
      setMessage("");
      
      const response = await apiFetch('/api/integrations/facebook/ai-analysis');
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to analyze Facebook messages");
      }
      
      const result = await response.json();
      setAnalysisResults(result.analysisResults);
      setMessage("Analysis completed successfully!");
      
      // Load sample conversations
      await loadSampleConversations();
      
    } catch (err) {
      setError(err.message || "An error occurred during analysis");
      console.error("Analysis error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Load sample conversations for detailed review
  const loadSampleConversations = async () => {
    try {
      const response = await apiFetch('/api/integrations/facebook/ai-sample-conversations');
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to load sample conversations");
      }
      
      const result = await response.json();
      setSampleConversations(result.conversations);
      
    } catch (err) {
      console.error("Error loading sample conversations:", err);
      // Don't set error state here to avoid overriding the main message
    }
  };

  // Setup database tables for AI parameters
  const setupParameterTables = async () => {
    try {
      setLoading(true);
      setError("");
      
      const response = await apiFetch('/api/integrations/facebook/ai-setup-tables', {
        method: 'POST'
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to setup parameter tables");
      }
      
      const result = await response.json();
      setMessage(result.message);
      
      // Load categories after table setup
      await loadCategories();
      await loadParameters();
      
    } catch (err) {
      setError(err.message || "An error occurred setting up parameter tables");
      console.error("Setup error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Load parameter categories
  const loadCategories = async () => {
    try {
      setError("");
      setMessage("");
      const response = await apiFetch('/api/integrations/facebook/ai-categories');
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to load categories");
      }
      
      const data = await response.json();
      setCategories(data.categories || []);
    } catch (err) {
      console.error("Error loading categories:", err);
      setError("Failed to load parameter categories: " + err.message);
    }
  };

  // Load parameters (with versioning)
  const loadParameters = async () => {
    try {
      setError("");
      const response = await apiFetch('/api/integrations/facebook/ai-parameters');
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to load parameters");
      }
      
      const data = await response.json();
      
      // Transform backend data to match frontend data structure
      const transformedParameters = (data.parameters || []).map(param => ({
        id: param.id,
        categoryId: param.category_id,
        category: param.category_name,
        name: param.name,
        description: param.description || '',
        isActive: param.is_active === 1,
        effectiveDate: param.effective_date,
        expirationDate: param.expiration_date || null,
        contextNotes: param.context_notes || '',
        hasSpreadsheet: param.has_file === 1,
        filename: param.original_filename || null,
        createdAt: param.created_at
      }));
      
      setParameters(transformedParameters);
    } catch (err) {
      console.error("Error loading parameters:", err);
    }
  };

  // Create parameter categories based on analysis
  const createCategories = async () => {
    try {
      if (!analysisResults || analysisResults.length === 0) {
        setError("Please run analysis first to identify message categories");
        return;
      }
      
      const categoriesToCreate = analysisResults.map(result => ({
        name: result.message_category,
        description: `Parameters for ${result.message_category.toLowerCase()} inquiries`
      }));
      
      setLoading(true);
      
      const response = await apiFetch('/api/integrations/facebook/ai-create-categories', {
        method: 'POST',
        body: JSON.stringify({ categories: categoriesToCreate }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to create parameter categories");
      }
      
      const result = await response.json();
      setMessage(result.message);
      
      // Load updated categories
      await loadCategories();
      
      // Switch to the parameters tab
      setActiveTab(1);
      
    } catch (err) {
      setError(err.message || "An error occurred creating parameter categories");
      console.error("Category creation error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Open dialog to add new category
  const openAddCategoryDialog = () => {
    setNewCategory({ name: '', description: '' });
    setCategoryDialogOpen(true);
  };

  // Add a new category
  const addCategory = async () => {
    if (!newCategory.name) {
      setError("Category name is required");
      return;
    }
    
    try {
      setLoading(true);
      setError("");
      
      const response = await apiFetch('/api/integrations/facebook/ai-create-categories', {
        method: 'POST',
        body: JSON.stringify({ 
          categories: [{ 
            name: newCategory.name.toUpperCase(), 
            description: newCategory.description || ""
          }] 
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to create category");
      }
      
      // Reload categories from server
      await loadCategories();
      
      setNewCategory({ name: '', description: '' });
      setCategoryDialogOpen(false);
      setMessage("Category added successfully");
    } catch (err) {
      setError(err.message || "An error occurred creating the category");
      console.error("Category creation error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Open parameter dialog
  const openParameterDialog = (categoryId, parameterId = null) => {
    setSelectedCategoryId(categoryId);
    setEditingParameterId(parameterId);
    
    if (parameterId) {
      // Editing existing parameter
      const parameter = parameters.find(p => p.id === parameterId);
      if (parameter) {
        setParameterData({
          name: parameter.name,
          description: parameter.description || '',
          isActive: parameter.isActive,
          effectiveDate: parameter.effectiveDate,
          expirationDate: parameter.expirationDate || '',
          contextNotes: parameter.contextNotes || '',
          file: null
        });
      }
    } else {
      // Creating new parameter
      setParameterData({
        name: '',
        description: '',
        isActive: true,
        effectiveDate: new Date().toISOString().split('T')[0], // Today's date
        expirationDate: '',
        contextNotes: '',
        file: null
      });
    }
    
    setParameterDialogOpen(true);
  };

  // Handle parameter file selection with robust validation
  const handleParameterFileSelect = (event) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      
      // Validate both file extension and MIME type
      const allowedExtensions = ['.xlsx', '.xls', '.csv', '.pdf'];
      const allowedMimeTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
        'application/vnd.ms-excel', // .xls
        'text/csv', // .csv
        'application/csv', // sometimes used for .csv
        'text/x-csv', // sometimes used for .csv
        'application/x-csv', // sometimes used for .csv
        'application/pdf' // PDF files
      ];
      
      const fileName = file.name.toLowerCase();
      const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
      
      // Log file details for debugging
      console.log("Selected file details:", { 
        name: file.name, 
        type: file.type, 
        size: file.size,
        extension: fileExtension
      });
      
      // Check if extension is allowed
      if (!allowedExtensions.includes(fileExtension)) {
        setError(`Invalid file extension. Only Excel (.xlsx, .xls), PDF (.pdf) and CSV (.csv) files are allowed.`);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      
      // Check if MIME type is allowed
      if (!allowedMimeTypes.includes(file.type) && file.type !== '') {
        console.warn(`File MIME type "${file.type}" might not be accepted by the server. Expected types:`, allowedMimeTypes);
        
        // If MIME type doesn't match extension expectations, show a warning
        if ((fileExtension === '.xlsx' && file.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') ||
            (fileExtension === '.xls' && file.type !== 'application/vnd.ms-excel') ||
            (fileExtension === '.csv' && !file.type.includes('csv') && !file.type.includes('text/plain'))) {
          setError(`Warning: The file appears to be ${fileExtension} but has MIME type "${file.type}". The server may reject this file. Please ensure you are uploading a valid Excel or CSV file.`);
          // Still allow the user to try uploading
        }
      }
      
      setParameterData({
        ...parameterData,
        file: file
      });
    }
  };

  // Parameter save function - simplified to use standard JSON for non-file data
  const saveParameter = async () => {
    if (!parameterData.name || !parameterData.effectiveDate) {
      setError("Parameter name and effective date are required");
      return;
    }
    
    try {
      setLoading(true);
      setError("");
      
      // Create endpoint URL
      const url = editingParameterId 
        ? `/api/integrations/facebook/ai-parameters/${editingParameterId}`
        : '/api/integrations/facebook/ai-parameters';
      
      // Special case: If we have a file, handle it differently
      if (parameterData.file) {
        // Proceed with caution using FormData for file upload
        await saveParameterWithFile(url);
      } else {
        // Use simple JSON for non-file parameters (more reliable)
        await saveParameterWithoutFile(url);
      }
      
      // Reload parameters from server
      await loadParameters();
      
      setMessage(editingParameterId ? "Parameter updated successfully" : "Parameter created successfully");
      
      // Reset form and close dialog
      setParameterData({
        name: '',
        description: '',
        isActive: true,
        effectiveDate: new Date().toISOString().split('T')[0],
        expirationDate: '',
        contextNotes: '',
        file: null
      });
      setParameterDialogOpen(false);
      
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      setError(err.message || "An error occurred saving the parameter");
      console.error("Parameter save error:", err);
    } finally {
      setLoading(false);
    }
  };
  
  // Helper function to save parameter without a file (using JSON)
  const saveParameterWithoutFile = async (url) => {
    // Create parameter data object
    const paramData = {
      categoryId: selectedCategoryId,
      name: parameterData.name,
      effectiveDate: parameterData.effectiveDate,
      isActive: parameterData.isActive ? 1 : 0  // Integer format (1/0)
    };
    
    // Add optional fields if present
    if (parameterData.description) {
      paramData.description = parameterData.description;
    }
    
    if (parameterData.expirationDate) {
      paramData.expirationDate = parameterData.expirationDate;
    }
    
    if (parameterData.contextNotes) {
      paramData.contextNotes = parameterData.contextNotes;
    }
    
    console.log("Saving parameter without file:", paramData);
    
    // Use apiFetch with JSON content
    const response = await apiFetch(url, {
      method: editingParameterId ? 'PUT' : 'POST',
      body: JSON.stringify(paramData)
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to save parameter");
    }
    
    const data = await response.json();
    console.log("Parameter saved successfully:", data);
    return data;
  };
  
  // Helper function to save parameter with a file (using FormData)
  const saveParameterWithFile = async (url) => {
    // Only use FormData when we actually have a file
    const formData = new FormData();
    
    // Add all required fields
    formData.append('categoryId', selectedCategoryId);
    formData.append('name', parameterData.name);
    formData.append('effectiveDate', parameterData.effectiveDate);
    formData.append('isActive', parameterData.isActive ? 1 : 0);
    
    // Add optional fields if present
    if (parameterData.description) {
      formData.append('description', parameterData.description);
    }
    
    if (parameterData.expirationDate) {
      formData.append('expirationDate', parameterData.expirationDate);
    }
    
    if (parameterData.contextNotes) {
      formData.append('contextNotes', parameterData.contextNotes);
    }
    
    // Validate file extension one last time before adding to FormData
    const fileName = parameterData.file.name.toLowerCase();
    const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
    
    if (!['.xlsx', '.xls', '.csv', '.pdf'].includes(fileExtension)) {
      throw new Error(`Only Excel (.xlsx, .xls), PDF (.pdf) and CSV (.csv) files are allowed. Found: ${fileExtension}`);
    }
    
    // Add the file as the last field
    formData.append('file', parameterData.file);
    
    // Debug - log all entries in the FormData to help troubleshoot
    console.log("FormData entries for upload:");
    for (let [key, value] of formData.entries()) {
      // For files, log the details separately
      if (key === 'file') {
        console.log(`${key}: ${value.name}, type: ${value.type}, size: ${value.size} bytes`);
      } else {
        console.log(`${key}: ${value}`);
      }
    }
    
    // Get token for auth
    const token = localStorage.getItem('token') || 'dummy-token';
    
    // Use native fetch with proper headers but no Content-Type (browser sets it)
    const response = await fetch(url, {
      method: editingParameterId ? 'PUT' : 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });
    
    // Check for non-OK responses and try to extract error details
    if (!response.ok) {
      const errorText = await response.text();
      console.error("Error response text:", errorText);
      
      if (errorText.trim().startsWith('{')) {
        try {
          const errorData = JSON.parse(errorText);
          throw new Error(errorData.error || `Server error: ${response.status}`);
        } catch (jsonError) {
          throw new Error(`Server error (${response.status}): ${errorText.substring(0, 100)}...`);
        }
      } else {
        // Extract error message from HTML if possible
        const errorMatch = errorText.match(/<pre>Error: ([^<]+)/);
        if (errorMatch && errorMatch[1]) {
          throw new Error(`Server error: ${errorMatch[1]}`);
        } else {
          throw new Error(`Server error: ${response.status} ${response.statusText}`);
        }
      }
    }
    
    // Parse successful response (carefully)
    let data;
    try {
      const responseText = await response.text();
      data = JSON.parse(responseText);
      console.log("Parameter with file saved successfully:", data);
    } catch (jsonError) {
      console.warn("Server returned non-JSON response:", jsonError);
      data = { message: "Operation completed successfully" };
    }
    
    return data;
  };

  // Toggle parameter status using JSON format (no files involved)
  const toggleParameterStatus = async (parameterId) => {
    try {
      setLoading(true);
      setError("");
      
      // Get current parameter
      const parameter = parameters.find(p => p.id === parameterId);
      if (!parameter) {
        throw new Error("Parameter not found");
      }
      
      // Create simple JSON data object
      const paramData = {
        categoryId: parameter.categoryId,
        name: parameter.name,
        effectiveDate: parameter.effectiveDate,
        // Toggle the active status - using integers (1/0)
        isActive: !parameter.isActive ? 1 : 0
      };
      
      // Add optional fields if present
      if (parameter.description) {
        paramData.description = parameter.description;
      }
      
      if (parameter.expirationDate) {
        paramData.expirationDate = parameter.expirationDate;
      }
      
      if (parameter.contextNotes) {
        paramData.contextNotes = parameter.contextNotes;
      }
      
      console.log("Toggling parameter status:", {
        id: parameterId,
        from: parameter.isActive ? 'active' : 'inactive',
        to: !parameter.isActive ? 'active' : 'inactive'
      });
      
      // Use apiFetch with JSON format (simpler approach)
      const response = await apiFetch(`/api/integrations/facebook/ai-parameters/${parameterId}`, {
        method: 'PUT',
        body: JSON.stringify(paramData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update parameter status");
      }
      
      const data = await response.json();
      console.log("Parameter status updated successfully:", data);
      
      // Reload parameters from server
      await loadParameters();
      
      setMessage(`Parameter ${!parameter.isActive ? 'activated' : 'deactivated'} successfully`);
    } catch (err) {
      setError(err.message || "An error occurred updating parameter status");
      console.error("Parameter status update error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Delete parameter
  const deleteParameter = async (parameterId) => {
    try {
      setLoading(true);
      setError("");
      
      const response = await apiFetch(`/api/integrations/facebook/ai-parameters/${parameterId}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to delete parameter");
      }
      
      // Reload parameters from server
      await loadParameters();
      
      setMessage("Parameter deleted successfully");
    } catch (err) {
      setError(err.message || "An error occurred deleting the parameter");
      console.error("Parameter deletion error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Open file preview dialog
  const openFilePreviewDialog = async (parameter) => {
    try {
      setLoading(true);
      setSelectedFile(parameter);
      
      const response = await apiFetch(`/api/integrations/facebook/ai-parameters/${parameter.id}/preview`);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to load file preview");
      }
      
      const result = await response.json();
      setFilePreview(result.preview || {});
      
      setFilePreviewDialogOpen(true);
    } catch (err) {
      setError(err.message || "An error occurred loading the file preview");
      console.error("File preview error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Get active parameters for a specific date
  const getActiveParametersForDate = (date) => {
    return parameters.filter(p => {
      const isActive = p.isActive;
      const isEffective = new Date(p.effectiveDate) <= new Date(date);
      const isNotExpired = !p.expirationDate || new Date(p.expirationDate) >= new Date(date);
      
      return isActive && isEffective && isNotExpired;
    });
  };

  // State to hold prompt details
  const [promptDetails, setPromptDetails] = useState(null);
  const [showPromptDialog, setShowPromptDialog] = useState(false);

  // Generate AI response for a test message
  const generateAIResponse = async () => {
    if (!testMessage) {
      setError("Please enter a test message");
      return;
    }
    
    try {
      setLoading(true);
      setError("");
      setResponseSource(null); // Reset response source when starting new request
      setPromptDetails(null); // Reset prompt details
      
      // Get active parameters for the selected test date
      const activeParameters = getActiveParametersForDate(testDate);
      
      // Make actual API call to backend with OpenAI integration
      // Request the full prompt details for debugging
      const response = await apiFetch('/api/integrations/facebook/ai-generate-response', {
        method: 'POST',
        body: JSON.stringify({
          message: testMessage,
          testDate: testDate,
          model: selectedModel, // Include selected model
          activeParameters: activeParameters.map(p => p.id),
          includePromptDetails: true // Request full prompt details 
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to generate AI response");
      }
      
      const result = await response.json();
      console.log("AI Response Result:", result); // Debug log
      
      // Update state with the actual AI-generated response
      setAiResponse(result.response);
      
      // Store the full prompt details if available, ensure structure matches expected format
      if (result.promptDetails) {
        // Make sure the object has the expected properties to prevent "systemPrompt is not defined" error
        const sanitizedDetails = {
          completeSystemPrompt: result.promptDetails.completeSystemPrompt || "",
          userMessage: result.promptDetails.userMessage || testMessage,
          // Add any other needed properties with fallbacks
          ...result.promptDetails
        };
        setPromptDetails(sanitizedDetails);
      }
      
      // Update response source information
      setResponseSource({
        usedOpenAI: result.openai?.used === true,
        tokenUsage: result.openai?.tokenUsage || null,
        parametersUsed: result.parametersUsed || [],
        hasPromptDetails: !!result.promptDetails
      });
      
      // Add to test results with metadata
      const newResult = {
        id: Date.now(),
        message: testMessage,
        response: result.response,
        timestamp: new Date().toLocaleString(),
        testDate: testDate,
        model: selectedModel,
        openaiUsed: result.openai?.used === true,
        parametersUsed: result.parametersUsed || [],
        metadata: result.openai?.tokenUsage || null,
        promptDetails: result.promptDetails
      };
      
      setTestResults([newResult, ...testResults]);
      
      // Directly update AI metadata in the database using our new endpoint
      // This ensures ai_processing_StopTime and other fields are properly recorded
      if (result.openai?.used && result.promptDetails) {
        try {
          // Create metadata object with all the values we're sending
          const metadataParams = {
            // Use conversation ID from the test result if available
            conversationId: result.conversationId || `test-conversation-${Date.now()}`,
            // Message ID if available
            messageId: result.messageId,
            // OpenAI metadata from the response - ensure names match backend expectations
            threadId: result.threadId || result.promptDetails.threadId,
            runId: result.runId || result.promptDetails.runId,
            assistantId: result.promptDetails.assistantId,
            assistantName: result.promptDetails.assistantName || 'Test Assistant',
            modelName: selectedModel,
            // Match parameter names with what backend expects (vectorStore_id not vectorStoreId)
            vectorStore_id: result.promptDetails.vectorStoreStatus?.vector_store_ids?.[0],
            vectorStore_Name: result.promptDetails.vectorStores?.[0]?.name || 'Test Vector Store',
            // Add ai_message_id if available
            aiMessageId: result.aiMessageId || result.promptDetails.aiMessageId,
            // Response content
            messageResponse: result.response,
            // User information
            username: localStorage.getItem('username') || 'test-user'
          };
          
          // Enhanced debugging - show ALL values being sent to update-ai-metadata endpoint
          console.log('\n===== SENDING AI METADATA TO SERVER - FULL DETAILS =====');
          console.log('Conversation ID:', metadataParams.conversationId);
          console.log('Message ID:', metadataParams.messageId || 'Not available');
          console.log('Thread ID:', metadataParams.threadId || 'Not available');
          console.log('Run ID:', metadataParams.runId || 'Not available');
          console.log('Assistant ID:', metadataParams.assistantId || 'Not available');
          console.log('Assistant Name:', metadataParams.assistantName || 'Not available');
          console.log('Model Name:', metadataParams.modelName || 'Not available');
          console.log('Vector Store ID:', metadataParams.vectorStore_id || 'Not available');
          console.log('Vector Store Name:', metadataParams.vectorStore_Name || 'Not available');
          console.log('AI Message ID:', metadataParams.aiMessageId || 'Not available');
          console.log('Response Length:', metadataParams.messageResponse?.length || 0);
          console.log('Username:', metadataParams.username || 'Not available');
          
          // Add stop time explicitly
          metadataParams.ai_processing_StopTime = new Date().toISOString();
          console.log('AI Processing Stop Time:', metadataParams.ai_processing_StopTime);
          console.log('=======================================================\n');
          
          const metadataResponse = await apiFetch('/api/integrations/facebook/assistant/update-ai-metadata', {
            method: 'POST',
            body: JSON.stringify(metadataParams)
          });
          
          if (metadataResponse.ok) {
            const metadataResult = await metadataResponse.json();
            console.log('AI metadata update successful:', metadataResult);
            
            // Add success indication to the UI
            setResponseSource(prevSource => ({
              ...prevSource,
              metadataUpdated: true,
              updateResult: metadataResult
            }));
          } else {
            const errorData = await metadataResponse.json();
            console.warn('AI metadata update failed:', errorData);
            
            // Add failure indication to the UI
            setResponseSource(prevSource => ({
              ...prevSource,
              metadataUpdated: false,
              updateError: errorData
            }));
            
            // Try again with direct message creation for test conversations
            if (metadataParams.conversationId.startsWith('test-')) {
              console.log('Retrying with explicit test conversation flag...');
              // Force conversationId to clearly indicate test
              metadataParams.conversationId = `test-${Date.now()}`;
              
              const retryResponse = await apiFetch('/api/integrations/facebook/assistant/update-ai-metadata', {
                method: 'POST',
                body: JSON.stringify(metadataParams)
              });
              
              if (retryResponse.ok) {
                const retryResult = await retryResponse.json();
                console.log('Retry successful:', retryResult);
                setResponseSource(prevSource => ({
                  ...prevSource,
                  metadataUpdated: true,
                  updateResult: retryResult,
                  retryWorked: true
                }));
              } else {
                console.error('Retry also failed');
              }
            }
          }
        } catch (metadataError) {
          console.error('Error updating AI metadata:', metadataError);
          // Continue even if metadata update fails - this is non-critical for testing
          
          // Add error indication to the UI
          setResponseSource(prevSource => ({
            ...prevSource,
            metadataUpdated: false,
            updateError: metadataError.message
          }));
        }
      }
      
    } catch (err) {
      console.error("Error generating AI response:", err);
      setError(err.message || "An error occurred generating the AI response");
    } finally {
      setLoading(false);
    }
  };

  // Clean up vector store by removing duplicate files
  const cleanupVectorStore = async () => {
    try {
      setVectorStoreCleanupStatus({
        isLoading: true,
        result: null,
        error: null
      });
      
      const response = await apiFetch('/api/integrations/facebook/cleanup-vector-store', {
        method: 'POST'
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to clean up vector store");
      }
      
      const result = await response.json();
      setVectorStoreCleanupStatus({
        isLoading: false,
        result: result,
        error: null
      });
      
      setMessage(`Vector store cleaned up successfully. Now contains ${result.fileCount} unique files.`);
    } catch (err) {
      console.error("Error cleaning up vector store:", err);
      setVectorStoreCleanupStatus({
        isLoading: false,
        result: null,
        error: err.message
      });
      setError(err.message || "An error occurred cleaning up the vector store");
    }
  };

  // Check OpenAI connection status
  const checkOpenAIConnection = async () => {
    try {
      setOpenAIStatus(prev => ({ ...prev, checking: true }));
      
      const response = await apiFetch('/api/integrations/facebook/ai-connection-status');
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to check OpenAI connection");
      }
      
      const result = await response.json();
      console.log("OpenAI connection status:", result);
      
      setOpenAIStatus({
        checking: false,
        ...result.status
      });
    } catch (err) {
      console.error("Error checking OpenAI connection:", err);
      setOpenAIStatus({
        checking: false,
        apiKeyExists: false,
        apiClientInitialized: false,
        connectionSuccessful: false,
        error: err.message
      });
    }
  };

  // Initialize component
  useEffect(() => {
    // Check if parameter tables exist and load data
    loadCategories();
    loadParameters();
    
    // Check OpenAI connection status
    checkOpenAIConnection();
  }, []);

  // Render component
  return (
    <Container>
      <Typography variant="h4" gutterBottom>Facebook AI Response Testing</Typography>
      <Typography variant="body1" paragraph>
        Analyze Facebook messages to create AI response parameters and test automated responses.
      </Typography>
      
      {/* OpenAI Connection Status */}
      {openAIStatus.checking ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Checking OpenAI connection status...
        </Alert>
      ) : openAIStatus.connectionSuccessful ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          ✅ Successfully connected to OpenAI API
        </Alert>
      ) : openAIStatus.apiClientInitialized ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          ⚠️ OpenAI client initialized but connection test failed: {openAIStatus.error}
        </Alert>
      ) : openAIStatus.apiKeyExists ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          ❌ OpenAI API key exists but client initialization failed. Check server logs for details.
        </Alert>
      ) : (
        <Alert severity="error" sx={{ mb: 2 }}>
          ❌ Not connected to OpenAI API. No API key found in environment variables.
        </Alert>
      )}
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="facebook ai tabs">
          <Tab label="Analysis" icon={<AnalyticsIcon />} iconPosition="start" />
          <Tab label="Parameters" icon={<CategoryIcon />} iconPosition="start" />
          <Tab label="Testing" icon={<TestIcon />} iconPosition="start" />
          <Tab label="Setup" icon={<SettingsIcon />} iconPosition="start" />
        </Tabs>
      </Box>
      
      {/* Analysis Tab */}
      {activeTab === 0 && (
        <Box>
          <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Message Analysis</Typography>
            <Typography variant="body2" paragraph>
              Analyze your Facebook message history to identify common inquiries and response patterns.
              This helps determine what parameters your AI response system will need.
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={runAnalysis}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AnalyticsIcon />}
              >
                {loading ? "Analyzing..." : "Analyze Facebook Messages"}
              </Button>
              
              {analysisResults && (
                <Button 
                  variant="outlined" 
                  color="primary" 
                  onClick={createCategories}
                  disabled={loading}
                  startIcon={<CategoryIcon />}
                >
                  Create Parameter Categories
                </Button>
              )}
            </Box>
            
            {analysisResults && (
              <Box>
                <Typography variant="h6" gutterBottom>Analysis Results</Typography>
                <Typography variant="body2" paragraph>
                  The following message categories were identified from your Facebook conversations:
                </Typography>
                
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Category</strong></TableCell>
                        <TableCell><strong>Messages</strong></TableCell>
                        <TableCell><strong>Conversations</strong></TableCell>
                        <TableCell><strong>Action</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analysisResults.map((result) => (
                        <TableRow key={result.message_category}>
                          <TableCell>{result.message_category}</TableCell>
                          <TableCell>{result.inquiry_count}</TableCell>
                          <TableCell>{result.conversation_count}</TableCell>
                          <TableCell>
                            <Button 
                              size="small" 
                              variant="outlined"
                              onClick={() => setSelectedCategory(result.message_category)}
                            >
                              View Examples
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {selectedCategory && analysisResults && (
                  <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="h6">Examples for: {selectedCategory}</Typography>
                      <Button size="small" onClick={() => setSelectedCategory(null)}>Close</Button>
                    </Box>
                    
                    <Divider sx={{ mb: 2 }} />
                    
                    {analysisResults
                      .filter(r => r.message_category === selectedCategory)
                      .map((result, index) => (
                        <Box key={index}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2" gutterBottom>Sample Questions:</Typography>
                              {result.sample_question_1 && (
                                <Paper variant="outlined" sx={{ p: 1, mb: 1, bgcolor: '#f5f5f5' }}>
                                  <Typography variant="body2">{result.sample_question_1}</Typography>
                                </Paper>
                              )}
                              {result.sample_question_2 && (
                                <Paper variant="outlined" sx={{ p: 1, mb: 1, bgcolor: '#f5f5f5' }}>
                                  <Typography variant="body2">{result.sample_question_2}</Typography>
                                </Paper>
                              )}
                              {result.sample_question_3 && (
                                <Paper variant="outlined" sx={{ p: 1, mb: 1, bgcolor: '#f5f5f5' }}>
                                  <Typography variant="body2">{result.sample_question_3}</Typography>
                                </Paper>
                              )}
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2" gutterBottom>Sample Responses:</Typography>
                              {result.sample_response_1 && (
                                <Paper variant="outlined" sx={{ p: 1, mb: 1, bgcolor: '#e3f2fd' }}>
                                  <Typography variant="body2">{result.sample_response_1}</Typography>
                                </Paper>
                              )}
                              {result.sample_response_2 && (
                                <Paper variant="outlined" sx={{ p: 1, mb: 1, bgcolor: '#e3f2fd' }}>
                                  <Typography variant="body2">{result.sample_response_2}</Typography>
                                </Paper>
                              )}
                              {result.sample_response_3 && (
                                <Paper variant="outlined" sx={{ p: 1, mb: 1, bgcolor: '#e3f2fd' }}>
                                  <Typography variant="body2">{result.sample_response_3}</Typography>
                                </Paper>
                              )}
                            </Grid>
                          </Grid>
                          
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>Suggested Parameter Types:</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {selectedCategory === 'PRICING' && (
                                <>
                                  <Chip label="Pricing spreadsheet" color="primary" size="small" />
                                  <Chip label="Effective dates" color="primary" size="small" />
                                </>
                              )}
                              {selectedCategory === 'BOOKING' && (
                                <>
                                  <Chip label="Available days" color="primary" size="small" />
                                  <Chip label="Lead time" color="primary" size="small" />
                                </>
                              )}
                              {selectedCategory === 'SERVICE_AREA' && (
                                <>
                                  <Chip label="Service area spreadsheet" color="primary" size="small" />
                                  <Chip label="City/zip code list" color="primary" size="small" />
                                </>
                              )}
                              {selectedCategory === 'HOURS' && (
                                <>
                                  <Chip label="Business hours" color="primary" size="small" />
                                  <Chip label="Seasonal schedules" color="primary" size="small" />
                                </>
                              )}
                              {selectedCategory === 'SERVICES' && (
                                <>
                                  <Chip label="Services spreadsheet" color="primary" size="small" />
                                  <Chip label="Service descriptions" color="primary" size="small" />
                                </>
                              )}
                              {(selectedCategory !== 'PRICING' && 
                                selectedCategory !== 'BOOKING' && 
                                selectedCategory !== 'SERVICE_AREA' &&
                                selectedCategory !== 'HOURS' &&
                                selectedCategory !== 'SERVICES') && (
                                  <Chip label="Parameter needs manual review" color="secondary" size="small" />
                              )}
                            </Box>
                          </Box>
                        </Box>
                      ))}
                  </Paper>
                )}
                
                {sampleConversations && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>Sample Conversation Threads</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" paragraph>
                        Below are complete conversation threads that show how inquiries flow:
                      </Typography>
                      
                      {Object.entries(sampleConversations).slice(0, 3).map(([convoId, messages]) => (
                        <Paper key={convoId} variant="outlined" sx={{ p: 2, mb: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Conversation ID: {convoId}
                          </Typography>
                          
                          {messages.map((msg, index) => (
                            <Box 
                              key={index} 
                              sx={{ 
                                display: 'flex', 
                                justifyContent: msg.sender_type === 'BUSINESS' ? 'flex-end' : 'flex-start',
                                mb: 1
                              }}
                            >
                              <Paper 
                                variant="outlined" 
                                sx={{ 
                                  p: 1, 
                                  maxWidth: '80%',
                                  bgcolor: msg.sender_type === 'BUSINESS' ? '#e3f2fd' : '#f5f5f5'
                                }}
                              >
                                <Typography variant="caption" display="block" color="text.secondary">
                                  {msg.sender_type === 'BUSINESS' ? 'Business' : msg.from_name} - {new Date(msg.created_time).toLocaleString()}
                                </Typography>
                                <Typography variant="body2">{msg.message}</Typography>
                              </Paper>
                            </Box>
                          ))}
                        </Paper>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                )}
              </Box>
            )}
          </Paper>
        </Box>
      )}
      
      {/* Parameters Tab */}
      {activeTab === 1 && (
        <Box>
          <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">AI Response Parameters</Typography>
              <Button 
                variant="contained" 
                color="primary" 
                startIcon={<AddIcon />}
                onClick={openAddCategoryDialog}
              >
                Add Category
              </Button>
            </Box>
            
            <Typography variant="body2" paragraph>
              Define versioned parameters with effective dates that will guide AI responses to Facebook messages.
              Each parameter can include both a spreadsheet and context notes to provide comprehensive information.
            </Typography>
            
            {/* Parameters By Category */}
            {categories.length === 0 ? (
              <Alert severity="info">
                No parameter categories defined yet. Run analysis first or add categories manually.
              </Alert>
            ) : (
              categories.map((category) => (
                <Box key={category.id} sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6">{category.name}</Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => openParameterDialog(category.id)}
                    >
                      Add Parameter
                    </Button>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {category.description}
                  </Typography>
                  
                  {parameters.filter(p => p.categoryId === category.id).length === 0 ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      No parameters defined for this category yet.
                    </Alert>
                  ) : (
                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Name</strong></TableCell>
                            <TableCell><strong>Status</strong></TableCell>
                            <TableCell><strong>Effective Date</strong></TableCell>
                            <TableCell><strong>Expiration Date</strong></TableCell>
                            <TableCell><strong>Spreadsheet</strong></TableCell>
                            <TableCell><strong>Actions</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {parameters
                            .filter(p => p.categoryId === category.id)
                            .sort((a, b) => new Date(b.effectiveDate) - new Date(a.effectiveDate))
                            .map((parameter) => (
                              <TableRow key={parameter.id}>
                                <TableCell>
                                  <Typography variant="body2">{parameter.name}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {parameter.description}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={parameter.isActive ? "Active" : "Inactive"} 
                                    color={parameter.isActive ? "success" : "default"}
                                    size="small"
                                    onClick={() => toggleParameterStatus(parameter.id)}
                                  />
                                </TableCell>
                                <TableCell>
                                  {new Date(parameter.effectiveDate).toLocaleDateString()}
                                </TableCell>
                                <TableCell>
                                  {parameter.expirationDate 
                                    ? new Date(parameter.expirationDate).toLocaleDateString() 
                                    : "No expiration"}
                                </TableCell>
                                <TableCell>
                                  {parameter.hasSpreadsheet ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      <Typography variant="body2" sx={{ mr: 1 }}>
                                        {parameter.filename}
                                      </Typography>
                                      <IconButton 
                                        size="small" 
                                        color="primary" 
                                        onClick={() => openFilePreviewDialog(parameter)}
                                      >
                                        <PreviewIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">
                                      No spreadsheet
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex' }}>
                                    <IconButton 
                                      size="small" 
                                      onClick={() => openParameterDialog(category.id, parameter.id)}
                                      title="Edit Parameter"
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton 
                                      size="small" 
                                      color="error" 
                                      onClick={() => deleteParameter(parameter.id)}
                                      title="Delete Parameter"
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </TableCell>
                              </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              ))
            )}
          </Paper>
        </Box>
      )}
      
      {/* Testing Tab */}
      {activeTab === 2 && (
        <Box>
          <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Test AI Responses</Typography>
              <Button 
                variant="outlined" 
                color={openAIStatus.connectionSuccessful ? "success" : "error"}
                size="small"
                startIcon={openAIStatus.connectionSuccessful ? <CheckIcon /> : <ErrorIcon />}
                onClick={checkOpenAIConnection}
              >
                {openAIStatus.connectionSuccessful ? "Connected to OpenAI" : "Not Connected to OpenAI"}
              </Button>
            </Box>
            <Typography variant="body2" paragraph>
              Test how the AI would respond to Facebook messages using parameters effective on a specific date.
              This lets you test both current responses and see how future parameter changes will affect responses.
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Enter a sample customer message"
                  multiline
                  rows={3}
                  value={testMessage}
                  onChange={(e) => setTestMessage(e.target.value)}
                  variant="outlined"
                  margin="normal"
                />
                
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 2 }}>
                  <TextField
                    type="date"
                    label="Test with parameters effective on"
                    value={testDate}
                    onChange={(e) => setTestDate(e.target.value)}
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    sx={{ mr: 2 }}
                  />
                  
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Active parameters for this date:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {getActiveParametersForDate(testDate).map(p => (
                        <Chip 
                          key={p.id} 
                          label={`${p.category}: ${p.name}`} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Box>
                </Box>
                
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={generateAIResponse}
                  disabled={!testMessage}
                >
                  Generate AI Response
                </Button>
              </Grid>
              
              <Grid item xs={12} md={6}>
                  <FormControl variant="outlined" sx={{ mb: 2, minWidth: 250 }}>
                    <InputLabel id="model-select-label">OpenAI Model</InputLabel>
                    <Select
                      labelId="model-select-label"
                      value={selectedModel}
                      onChange={(e) => setSelectedModel(e.target.value)}
                      label="OpenAI Model"
                    >
                      <MenuItem value="gpt-3.5-turbo">GPT-3.5 Turbo</MenuItem>
                      <MenuItem value="gpt-4">GPT-4</MenuItem>
                      <MenuItem value="gpt-4-turbo-preview">GPT-4 Turbo</MenuItem>
                      <MenuItem value="gpt-3.5-turbo-0125">GPT-3.5 Turbo (0125)</MenuItem>
                    </Select>
                    <FormHelperText>Different models may respond differently</FormHelperText>
                  </FormControl>
                  
                  <Typography variant="subtitle2" gutterBottom>AI Response:</Typography>
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 2, 
                      minHeight: '100px', 
                      bgcolor: aiResponse ? '#e3f2fd' : '#f5f5f5',
                      mb: 2
                    }}
                  >
                  {aiResponse ? (
                    <>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                        {aiResponse}
                      </Typography>
                      
                      {responseSource && (
                        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                          <Chip 
                            label={responseSource.usedOpenAI ? "Generated by OpenAI" : "Generated by rule-based system"} 
                            color={responseSource.usedOpenAI ? "success" : "warning"} 
                            size="small" 
                            sx={{ mr: 1, mb: 1 }}
                          />
                          
                          {responseSource.usedOpenAI && responseSource.tokenUsage && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              Tokens used: {responseSource.tokenUsage.total_tokens} 
                              (prompt: {responseSource.tokenUsage.prompt_tokens}, 
                              completion: {responseSource.tokenUsage.completion_tokens})
                            </Typography>
                          )}
                          
                          {responseSource.parametersUsed && responseSource.parametersUsed.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="caption" display="block">
                                Parameters used:
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {responseSource.parametersUsed.map(param => (
                                  <Chip 
                                    key={param.id || param} 
                                    label={typeof param === 'object' ? param.name : param} 
                                    size="small" 
                                    variant="outlined" 
                                    sx={{ fontSize: '0.7rem' }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}
                          
                          {responseSource.hasPromptDetails && (
                            <Box sx={{ mt: 1 }}>
                              <Button 
                                variant="outlined" 
                                size="small" 
                                onClick={() => setShowPromptDialog(true)}
                                startIcon={<AnalyticsIcon />}
                              >
                                Show Prompt Details
                              </Button>
                            </Box>
                          )}
                        </Box>
                      )}
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      AI response will appear here after generation
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Paper>
          
          {testResults.length > 0 && (
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Test History</Typography>
              
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Customer Message</strong></TableCell>
                      <TableCell><strong>AI Response</strong></TableCell>
                      <TableCell><strong>Source</strong></TableCell>
                      <TableCell><strong>Model</strong></TableCell>
                      <TableCell><strong>Test Date</strong></TableCell>
                      <TableCell><strong>Timestamp</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {testResults.map((test) => (
                      <TableRow key={test.id}>
                        <TableCell>
                          <Typography variant="body2">{test.message}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                            {test.response}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={test.openaiUsed ? "OpenAI" : "Rule-based"} 
                            color={test.openaiUsed ? "success" : "warning"} 
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {test.model || "Default"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(test.testDate).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{test.timestamp}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Box>
      )}
      
      {/* Setup Tab */}
      {activeTab === 3 && (
        <Box>
          <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Setup AI Testing Environment</Typography>
            <Typography variant="body2" paragraph>
              Configure and initialize the database tables required for AI response testing.
            </Typography>
            
            <Button 
              variant="contained" 
              color="primary" 
              onClick={setupParameterTables}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SettingsIcon />}
              sx={{ mr: 2 }}
            >
              Setup Database Tables
            </Button>
            
            <Button 
              variant="outlined" 
              color="primary" 
              startIcon={<RefreshIcon />}
              onClick={() => {
                loadCategories();
                loadParameters();
              }}
            >
              Refresh Data
            </Button>
          </Paper>
          
          {/* Vector Store Cleanup Section */}
          <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Vector Store Management</Typography>
            <Typography variant="body2" paragraph>
              Clean up the OpenAI vector store to remove duplicate files. This helps resolve issues where 
              the same documents are uploaded multiple times, causing unnecessary duplication.
            </Typography>
            
            <Button
              variant="contained"
              color="secondary"
              onClick={cleanupVectorStore}
              disabled={vectorStoreCleanupStatus.isLoading}
              startIcon={vectorStoreCleanupStatus.isLoading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
              sx={{ mb: 2 }}
            >
              {vectorStoreCleanupStatus.isLoading ? "Cleaning up..." : "Clean Up Vector Store"}
            </Button>
            
            {vectorStoreCleanupStatus.result && (
              <Alert severity="success" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Success:</strong> Vector store cleaned up successfully.
                </Typography>
                <Typography variant="body2">
                  Vector Store ID: {vectorStoreCleanupStatus.result.vectorStoreId}
                </Typography>
                <Typography variant="body2">
                  File Count: {vectorStoreCleanupStatus.result.fileCount} unique files
                </Typography>
              </Alert>
            )}
            
            {vectorStoreCleanupStatus.error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Error:</strong> {vectorStoreCleanupStatus.error}
                </Typography>
              </Alert>
            )}
          </Paper>
          
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Implementation Steps</Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="1. Analyze Facebook Messages" 
                  secondary="Run the analysis to identify common questions and patterns"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="2. Create Parameter Categories" 
                  secondary="Define categories for different types of parameters"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="3. Create Parameters with Versions" 
                  secondary="Define parameters with effective dates and optional expiration dates"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="4. Upload Spreadsheets" 
                  secondary="Attach spreadsheets to parameters for complex data structures"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="5. Add Context Notes" 
                  secondary="Provide additional guidance for AI interpretation"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="6. Test Responses with Date Selection" 
                  secondary="Test how responses change based on effective parameter versions"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="7. Evaluate and Refine" 
                  secondary="Improve parameters based on test results"
                />
              </ListItem>
            </List>
          </Paper>
        </Box>
      )}
      
      {/* Add Category Dialog */}
      <Dialog open={categoryDialogOpen} onClose={() => setCategoryDialogOpen(false)}>
        <DialogTitle>Add Parameter Category</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Category Name"
            fullWidth
            variant="outlined"
            value={newCategory.name}
            onChange={(e) => setNewCategory({...newCategory, name: e.target.value})}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            variant="outlined"
            value={newCategory.description}
            onChange={(e) => setNewCategory({...newCategory, description: e.target.value})}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryDialogOpen(false)}>Cancel</Button>
          <Button onClick={addCategory} variant="contained" color="primary">Add</Button>
        </DialogActions>
      </Dialog>
      
      {/* Parameter Dialog */}
      <Dialog open={parameterDialogOpen} onClose={() => setParameterDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingParameterId ? "Edit Parameter" : "Add Parameter"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                autoFocus
                margin="dense"
                label="Parameter Name"
                fullWidth
                required
                variant="outlined"
                value={parameterData.name}
                onChange={(e) => setParameterData({...parameterData, name: e.target.value})}
                helperText="Name for this parameter version (e.g., 'Summer 2025 Pricing')"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                margin="dense"
                label="Description (Optional)"
                fullWidth
                variant="outlined"
                value={parameterData.description}
                onChange={(e) => setParameterData({...parameterData, description: e.target.value})}
                helperText="Brief description of this parameter version"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                type="date"
                margin="dense"
                label="Effective Date"
                required
                fullWidth
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                value={parameterData.effectiveDate}
                onChange={(e) => setParameterData({...parameterData, effectiveDate: e.target.value})}
                helperText="Date when this parameter version becomes active"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                type="date"
                margin="dense"
                label="Expiration Date (Optional)"
                fullWidth
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                value={parameterData.expirationDate}
                onChange={(e) => setParameterData({...parameterData, expirationDate: e.target.value})}
                helperText="Leave blank if no expiration"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl component="fieldset" sx={{ mt: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={parameterData.isActive}
                      onChange={(e) => setParameterData({...parameterData, isActive: e.target.checked})}
                    />
                  }
                  label="Active"
                />
                <FormHelperText>Inactive parameters won't be used for AI responses</FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ mt: 1, mb: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Upload Spreadsheet (Optional)
                </Typography>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  sx={{ mb: 1 }}
                >
                  Select File
                  <VisuallyHiddenInput 
                    type="file" 
                    onChange={handleParameterFileSelect} 
                    accept=".xlsx,.xls,.csv,.pdf"
                    ref={fileInputRef}
                  />
                  <FormHelperText>
                    Only Excel (.xlsx, .xls), PDF (.pdf) and CSV (.csv) files are accepted
                  </FormHelperText>
                </Button>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {parameterData.file ? `Selected: ${parameterData.file.name}` : (
                    editingParameterId && parameters.find(p => p.id === editingParameterId)?.hasSpreadsheet 
                      ? `Current file: ${parameters.find(p => p.id === editingParameterId)?.filename}` 
                      : 'No file selected'
                  )}
                </Typography>
                <FormHelperText>
                  Upload a spreadsheet containing detailed parameter data
                </FormHelperText>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                label="Context Notes"
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                value={parameterData.contextNotes}
                onChange={(e) => setParameterData({...parameterData, contextNotes: e.target.value})}
                helperText="Additional context to help the AI interpret this parameter and spreadsheet (if any)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setParameterDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={saveParameter} 
            variant="contained" 
            color="primary"
            disabled={!parameterData.name || !parameterData.effectiveDate}
          >
            {editingParameterId ? "Save Changes" : "Add Parameter"}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* File Preview Dialog */}
      <Dialog 
        open={filePreviewDialogOpen} 
        onClose={() => setFilePreviewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Spreadsheet Preview: {selectedFile?.filename}
        </DialogTitle>
        <DialogContent>
          {selectedFile && (
            <Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2">Parameter: {selectedFile.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Effective: {new Date(selectedFile.effectiveDate).toLocaleDateString()}
                  {selectedFile.expirationDate ? ` to ${new Date(selectedFile.expirationDate).toLocaleDateString()}` : ' (no expiration)'}
                </Typography>
              </Box>
              
              {selectedFile.contextNotes && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f8f9fa' }}>
                  <Typography variant="subtitle2" gutterBottom>Context Notes:</Typography>
                  <Typography variant="body2">{selectedFile.contextNotes}</Typography>
                </Paper>
              )}
              
              {filePreview && Object.entries(filePreview).map(([sheetName, rows]) => (
                <Box key={sheetName} sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Sheet: {sheetName}
                  </Typography>
                  
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {Object.keys(rows[0] || {}).map((header, i) => (
                            <TableCell key={i}><strong>{header}</strong></TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.map((row, rowIndex) => (
                          <TableRow key={rowIndex}>
                            {Object.values(row).map((cell, cellIndex) => (
                              <TableCell key={cellIndex}>{cell}</TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFilePreviewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      
      {/* Prompt Details Dialog - Complete Raw Data Display */}
      <Dialog
        open={showPromptDialog}
        onClose={() => setShowPromptDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Complete OpenAI Prompt Data
        </DialogTitle>
        <DialogContent>
          {promptDetails ? (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                This dialog shows the exact data sent to OpenAI, including all parameter information and instructions. 
                Use this to understand why the AI may or may not be utilizing specific data like breed information.
              </Alert>
              
              <Typography variant="subtitle1" gutterBottom>System Prompt (Complete Instructions)</Typography>
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 2, 
                  mb: 3,
                  bgcolor: '#f5f5f5',
                  maxHeight: '400px',
                  overflow: 'auto'
                }}
              >
                <Typography 
                  component="pre" 
                  sx={{ 
                    fontFamily: 'monospace', 
                    fontSize: '0.85rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}
                >
                  {promptDetails && (promptDetails.completeSystemPrompt || "No system prompt available")}
                </Typography>
              </Paper>
              
              <Typography variant="subtitle1" gutterBottom>User Message</Typography>
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 2, 
                  mb: 3,
                  bgcolor: '#f5f5f5'
                }}
              >
                <Typography 
                  component="pre" 
                  sx={{ 
                    fontFamily: 'monospace', 
                    fontSize: '0.85rem',
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {promptDetails && promptDetails.userMessage ? promptDetails.userMessage : "No user message available"}
                </Typography>
              </Paper>
              
              {/* Vector Store and File Attachment Status */}
              {promptDetails && promptDetails.vectorStores && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>Vector Store & File Attachment Status</Typography>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ mb: 2 }}>
                      <Alert severity={promptDetails.vectorStoreStatus?.linked_to_assistant ? "success" : "warning"}>
                        <Typography variant="body2">
                          {promptDetails.vectorStoreStatus?.linked_to_assistant 
                            ? `✓ Vector store successfully linked to assistant. ${promptDetails.vectorStoreStatus?.file_count || 0} files available.` 
                            : "⚠️ No vector store linked to assistant. Files may not be accessible by the AI."}
                        </Typography>
                      </Alert>
                    </Box>
                    
                    {promptDetails.vectorStores && promptDetails.vectorStores.length > 0 ? (
                      promptDetails.vectorStores.map((vs, index) => (
                        <Accordion key={vs.id || index} sx={{ mb: 1 }}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                              <Typography>{vs.name || `Vector Store ${index + 1}`}</Typography>
                              <Chip 
                                label={`${vs.file_count || 0} files`} 
                                color="primary" 
                                size="small" 
                                sx={{ ml: 1 }}
                              />
                              <Chip 
                                label={vs.status} 
                                color={vs.status === 'available' ? "success" : "error"} 
                                size="small" 
                                sx={{ ml: 1 }}
                              />
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            {vs.files && vs.files.length > 0 ? (
                              <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell><strong>Filename</strong></TableCell>
                                      <TableCell><strong>Status</strong></TableCell>
                                      <TableCell><strong>Created</strong></TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {vs.files.map((file) => (
                                      <TableRow key={file.id}>
                                        <TableCell>{file.filename}</TableCell>
                                        <TableCell>
                                          <Chip 
                                            label={file.status} 
                                            color={file.status === 'processed' ? "success" : "warning"} 
                                            size="small" 
                                          />
                                        </TableCell>
                                        <TableCell>
                                          {new Date(file.created_at).toLocaleString()}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No files in this vector store.
                              </Typography>
                            )}
                          </AccordionDetails>
                        </Accordion>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No vector stores found. Files may not be properly attached to the assistant.
                      </Typography>
                    )}
                  </Paper>
                </Box>
              )}
              
              {/* Database Parameters Section */}
              {promptDetails && promptDetails.databaseParameters && promptDetails.databaseParameters.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>Database Parameters with Files</Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Parameter</strong></TableCell>
                          <TableCell><strong>Category</strong></TableCell>
                          <TableCell><strong>File</strong></TableCell>
                          <TableCell><strong>Status</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {promptDetails.databaseParameters
                          .filter(p => p.has_file)
                          .map((param, index) => (
                            <TableRow key={param.id || index}>
                              <TableCell>{param.name}</TableCell>
                              <TableCell>{param.category}</TableCell>
                              <TableCell>{param.filename}</TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  {param.file_in_vector_store ? (
                                    <Chip 
                                      label="In Vector Store" 
                                      color="success" 
                                      size="small" 
                                      icon={<CheckIcon />}
                                    />
                                  ) : (
                                    <Chip 
                                      label="Not in Vector Store" 
                                      color="error" 
                                      size="small" 
                                      icon={<ErrorIcon />}
                                    />
                                  )}
                                  
                                  {param.file_used_in_messages && (
                                    <Chip 
                                      label="Used in Messages" 
                                      color="info" 
                                      size="small" 
                                    />
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              <Typography variant="subtitle1" gutterBottom>Complete Raw Prompt Data</Typography>
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 2,
                  bgcolor: '#f5f5f5',
                  maxHeight: '300px',
                  overflow: 'auto'
                }}
              >
                <Typography 
                  component="pre" 
                  sx={{ 
                    fontFamily: 'monospace', 
                    fontSize: '0.85rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}
                >
                  {promptDetails ? JSON.stringify(promptDetails, null, 2) : "No prompt details available"}
                </Typography>
              </Paper>
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Tip: Search the system prompt for specific information like "Pit Bull" or "coat type" to see how it's being incorporated.
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <CircularProgress size={40} sx={{ mb: 2 }} />
              <Typography>Prompt details not available</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPromptDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FacebookAITesting;